'use client';

import { Container } from '@mv/web-ui';
import Image from 'next/image';
import { useDataTags } from '@/modules/tracking/analytics/useDataTags';
import { assertPayloadType } from '@/modules/utils/assert';
import BackgroundIllustration from '@/payload/components/BackgroundIllustrations/BackgroundIllustration';
import { CallToActionGroup } from '@/payload/components/CallToActionGroup/CallToActionGroup';
import RichHeading from '@/payload/components/RichHeading/RichHeading';
import RichText from '@/payload/components/RichText/RichText';
import type { Media, HeroWithImageBlock as Props } from '@/payload/types';
import styles from './HeroWithImage.module.scss';

function HeroWithImage({ blockType, id, background, media, heading, content, links, link_size }: Props) {
	const { blockRef, labelRef, contentRef } = useDataTags(blockType, id);

	return (
		<Container data-component="HeroWithImageBlock" ref={blockRef}>
			<div className={styles.heroWithImage}>
				<BackgroundIllustration background={background} />
				{media &&
					(() => {
						assertPayloadType<Media>(media);
						return (
							<div className={styles.mediaBlock}>
								<MediaBlock media={media} />
							</div>
						);
					})()}
				<div className={styles.introBlock}>
					<div className={styles.text}>
						<RichHeading content={heading.text} options={{ tag: 'h1' }} ref={labelRef} />
						{content && <RichText content={content} ref={contentRef} />}
					</div>
					{links && links.length > 0 && <CallToActionGroup className={styles.links} links={links} size={link_size} />}
				</div>
			</div>
		</Container>
	);
}

function MediaBlock({ media }: { media: Media }) {
	const { mimeType } = media;

	if (mimeType?.startsWith('image/')) return <ImageBlock media={media} />;

	return null;
}

interface ImageBlockProps {
	media: Media;
}
function ImageBlock({ media }: ImageBlockProps) {
	if (!media.url) return null;

	return (
		<figure>
			<Image
				src={media.url}
				alt={media.alt || ''}
				className={styles.image}
				width={(400 / 3) * 4}
				height={400}
				priority
				fetchPriority="high"
				loading="eager"
			/>
			{media.caption && (
				<figcaption className={styles.caption}>
					<RichText content={media.caption} />
				</figcaption>
			)}
		</figure>
	);
}

export default HeroWithImage;
